/**
 * tse:question option objects and the kind:"answer" reply (tse_question.schema.json, tse_message.schema.json).
 *
 * W2 sends options as {option_id, label} with a question_id; legacy questions send bare strings. The
 * question card keeps rendering strings, so options are normalised to labels on arrival and the ids are
 * kept beside them. An answer goes out as kind "answer" with answer_to + option_id (single) or option_ids
 * (multi) only when the question carried ids; otherwise the legacy caption path is used unchanged.
 */

const isOptionObject = (o) => !!o && typeof o === "object" && typeof o.option_id === "string" && typeof o.label === "string";

/** {...payload, options: [labels], optionObjects: [{option_id, label}] | undefined}. Legacy payloads are returned as is. */
export function normalizeQuestion(payload) {
  if (!payload || !Array.isArray(payload.options) || !payload.options.some(isOptionObject)) return payload;
  const optionObjects = payload.options.filter(isOptionObject);
  return { ...payload, options: optionObjects.map((o) => o.label), optionObjects };
}

/** The labels a QuestionCard answer stands for: multi answers arrive as a JSON array of labels. */
function answerLabels(question, answer) {
  if (question?.input_type === "multi") {
    try {
      const parsed = JSON.parse(answer);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (_) {
      return [String(answer)];
    }
  }
  return [String(answer)];
}

/**
 * The tse:message fields for a kind:"answer" reply, or null to use the legacy text path.
 * @returns {{kind: "answer", answer_to: string, message: string, option_id?: string, option_ids?: string[]}|null}
 */
export function buildAnswer(question, answer) {
  const objects = question?.optionObjects;
  if (!question?.question_id || !Array.isArray(objects) || !objects.length) return null;
  const labels = answerLabels(question, answer);
  const byLabel = new Map(objects.map((o) => [o.label, o.option_id]));
  const ids = labels.map((l) => byLabel.get(l)).filter(Boolean);
  if (ids.length !== labels.length) return null;
  const message = labels.join(", ");
  if (question.input_type === "multi") return { kind: "answer", answer_to: question.question_id, option_ids: ids, message };
  return { kind: "answer", answer_to: question.question_id, option_id: ids[0], message };
}
